import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatCZK, formatDate, plur } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Search } from "lucide-react";
import { toast } from "sonner";

export function AssignProjectsSheet({ investorId, open, onOpenChange, onAssigned }) {
  const [desktop, setDesktop] = useState(typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);
  const [pool, setPool] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = (e) => setDesktop(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQ(""); setSel([]);
    api.get(`/investors/${investorId}/assignable`)
      .then((r) => setPool(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [open, investorId]);

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return pool;
    return pool.filter((p) => `${p.name} ${p.code || ""}`.toLowerCase().includes(n));
  }, [pool, q]);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const assign = async () => {
    if (!sel.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/investors/${investorId}/projects`, { project_ids: sel });
      toast.success(sel.length === 1 ? "Zakázka přiřazena." : `Přiřazeno ${plur(sel.length, "zakázka", "zakázky", "zakázek")}.`);
      onAssigned?.(data);          // server vrací rovnou aktualizovaného investora
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={desktop ? "right" : "bottom"} data-testid="assign-projects-sheet"
        className={desktop ? "w-[min(460px,100%)] sm:max-w-[460px] p-0 flex flex-col"
                           : "h-[85vh] rounded-t-[20px] p-0 flex flex-col"}>
        <SheetHeader className="shrink-0 px-5 pt-5 pb-4 border-b border-border text-left">
          <SheetTitle className="font-heading text-xl font-black tracking-tight">Přiřadit zakázku</SheetTitle>
          <SheetDescription>
            {plur(pool.length, "zakázka", "zakázky", "zakázek")} bez investora — vyber, kterou přiřadit.
          </SheetDescription>
          <div className="relative mt-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="assign-search"
              placeholder="Hledat podle názvu nebo kódu…" className="h-11 pl-9 w-full min-w-0" />
          </div>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-2">
          {shown.map((p) => {
            const on = sel.includes(p.id);
            return (
              <button key={p.id} type="button" onClick={() => toggle(p.id)} data-testid={`assign-row-${p.id}`}
                className={`w-full flex items-center gap-3 p-3 rounded-[14px] border text-left transition-colors ${on ? "border-brand bg-brand/5" : "border-border hover:border-brand/50"}`}>
                <span className={`w-[22px] h-[22px] shrink-0 rounded-[7px] grid place-items-center border ${on ? "bg-brand border-brand text-white" : "border-border"}`}>
                  {on && <Check size={14} strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-bold truncate">{p.name}</span>
                  <span className="block text-xs text-muted-foreground truncate">
                    {p.code ? `${p.code} · ` : ""}{p.start_date ? formatDate(p.start_date) : "—"}
                    {p.contract_price ? ` · ${formatCZK(p.contract_price)}` : ""}
                  </span>
                </span>
                <StatusBadge status={p.status} />
              </button>
            );
          })}
          {shown.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {q.trim() ? "Nic neodpovídá hledání." : "Všechny ostatní zakázky už investora mají."}
            </p>
          )}
        </div>

        <div className="shrink-0 px-5 py-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))] border-t border-border flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-[13px] text-muted-foreground">
            {sel.length ? `Vybráno ${sel.length}` : "Vyber jednu nebo víc zakázek."}
          </span>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11 w-full sm:w-auto font-bold">Zrušit</Button>
            <Button onClick={assign} disabled={busy || !sel.length} data-testid="assign-confirm"
              className="h-11 w-full sm:w-auto font-bold">
              {busy ? "Přiřazuji…" : sel.length > 1 ? `Přiřadit vybrané (${sel.length})` : "Přiřadit"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
